import { useState, useEffect, useMemo } from 'react';
import {
  Users, X, Search, Filter,
  CheckCircle, UserPlus,
  UserMinus
} from 'lucide-react';
import { usePatient } from '../../context/patientContext';
import { useHasAnyPermission } from '../../hooks/usePermission';
import {
  fetchPatientProviderAssignments,
  assignProviderToPatient,
  unassignProviderFromPatient
} from '../../services/admin/patient.service';
import { fetchAllProviders } from '../../services/admin/user.service';
import { isApiError } from '../../dtos/common/api.dto';
import { getButtonClasses, getPaginationButtonClasses } from '../../hooks/useMobileUtils';
import { GenericListView, LoadingSpinner, DisciplinePill, getDisciplinePillClassName, getDisciplineConfig, ProviderAvatar, ExpirationPill, FilterConfig } from '../common';
import { disciplineCodes, disciplineForProviderType } from '../../constants/disciplines';

// Helper function to format dates
const formatDate = (dateString: string, isEndDate: boolean = false): string => {
  if (!dateString) return isEndDate ? 'No Expiration' : 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

interface CareTeamProps {
  expanded: boolean;
  toggleExpanded: () => void;
}

interface Provider {
  id: number | string;
  name: string;
  discipline: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  startDate?: string;
  endDate?: string;
  isAssigned?: boolean;
  userId?: string;
  assignmentId?: string;
  assignmentType?: string;
  photoUrl?: string;
  expirationAlertLevel?: 'green' | 'yellow' | 'orange' | 'red' | 'expired' | 'none';
  expirationDaysRemaining?: number | null;
  expirationIsExpired?: boolean;
}

/**
 * Discipline a provider fills (Phase 20A: registry; LVN → RN, PTA → PT). Guardians are Caregiver;
 * legacy free text is matched by keyword.
 */
const normalizeDiscipline = (providerType: string | undefined | null): string => {
  if (!providerType) return 'Unknown';
  const upper = providerType.trim().toUpperCase();
  if (upper === 'PATIENTFAMILY' || upper === 'CAREGIVER' || upper === 'GUARDIAN') return 'Caregiver';
  const fromRegistry = disciplineForProviderType(upper);
  if (fromRegistry) return fromRegistry;
  if (upper.includes('NURS')) return 'RN';
  if (upper.includes('PHYSICAL')) return 'PT';
  if (upper.includes('OCCUPATIONAL')) return 'OT';
  if (upper.includes('SPEECH')) return 'ST';
  if (upper.includes('SOCIAL')) return 'MSW';
  if (upper.includes('AIDE')) return 'HHA';
  if (upper.includes('CASE')) return 'CASE_MGR';
  return providerType;
};

// Sample provider component to be used in the Providers section
const CareTeam = ({ expanded, toggleExpanded }: CareTeamProps) => {
  // Get selected patient from context
  const { selectedPatient } = usePatient();

  // Permission checks for Care Team
  const canReadProviders = useHasAnyPermission([
    'patientmr:phi:providers:read' as any,
    'patientmr:phi:providers:readall' as any
  ]);

  const canUpdateProviders = useHasAnyPermission([
    'patientmr:phi:providers:update' as any,
    'patientmr:phi:providers:updateall' as any
  ]);

  // Read-only when user can read but cannot update
  const isReadOnlyView = canReadProviders && !canUpdateProviders;

  // Provider types/disciplines — canonical codes matching DisciplinePill config
  const providerDisciplines = useMemo(() => [...disciplineCodes(), 'CASE_MGR'], []);

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [assignedProviders, setAssignedProviders] = useState<Provider[]>([]);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // List of providers fetched from API
  const [availableProviders, setAvailableProviders] = useState<Provider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState<boolean>(false);
  const [selectedDisciplineFilter, setSelectedDisciplineFilter] = useState<string>("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [assignedSearchTerm, setAssignedSearchTerm] = useState<string>('');
  const [assignedDisciplineFilter, setAssignedDisciplineFilter] = useState<string>('All');

  // Pagination state for Add Provider dialog
  const [providerDialogPage, setProviderDialogPage] = useState<number>(1);
  const [providersPerPage] = useState<number>(10);

  // Pagination state for Care Team list
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [careTeamPage, setCareTeamPage] = useState<number>(1);

  // Check for mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // Standard breakpoint for mobile
    };

    checkIsMobile(); // Check on initial load
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Fetch assigned providers when the component mounts or when the selected patient changes
  useEffect(() => {
    // Reset component state when patient changes
    setError(null);
    setIsLoading(false);
    setSelectedProvider(null);
    setShowAddModal(false);
    setAvailableProviders([]);
    setCareTeamPage(1); // Reset Care Team pagination
    setProviderDialogPage(1); // Reset Provider dialog pagination

    if (selectedPatient && selectedPatient.id) {
      fetchAssignedProviders(selectedPatient.id.toString());
    } else {
      // If no patient is selected, clear the assigned providers
      setAssignedProviders([]);
    }
  }, [selectedPatient]);

  // Function to fetch assigned providers from the API
  const fetchAssignedProviders = async (patientId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const assignments = await fetchPatientProviderAssignments(patientId);

      // Convert provider assignments to our Provider format
      // Using provider details directly from the API response
      const providers: Provider[] = assignments.map(assignment => {
        // Get provider details from the API response
        const providerDetails = assignment.provider || {
          firstName: 'Unknown',
          lastName: 'Provider',
          discipline: assignment.assignmentType || 'Unknown'
        };

        // Use safe property access to avoid TypeScript errors
        const firstName = providerDetails?.firstName || 'Unknown';
        const lastName = providerDetails?.lastName || 'Provider';
        const discipline = normalizeDiscipline(providerDetails?.discipline || assignment.assignmentType);

        // Only access middleName if it exists on the provider object
        let middleName: string | undefined = undefined;
        if (assignment.provider && 'middleName' in assignment.provider) {
          middleName = assignment.provider.middleName;
        }

        return {
          id: assignment.assignmentId,
          userId: assignment.userId,
          assignmentId: assignment.assignmentId,
          name: `${firstName} ${lastName}`,
          discipline: discipline,
          firstName,
          lastName,
          middleName,
          startDate: assignment.startDate,
          endDate: assignment.endDate || '',
          isAssigned: true,
          assignmentType: assignment.assignmentType,
          expirationAlertLevel: assignment.expirationAlertLevel,
          expirationDaysRemaining: assignment.expirationDaysRemaining,
          expirationIsExpired: assignment.expirationIsExpired
        };
      });

      setAssignedProviders(providers);
      setHasLoadedOnce(true);
    } catch (err) {
      console.error('Error fetching provider assignments:', err);
      setError('Failed to load providers. Please try again.');
      // Fallback to mock data if needed
      setAssignedProviders([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh handler
  const handleRefresh = async () => {
    if (!selectedPatient?.id) return;
    setRefreshing(true);
    setError(null);
    try {
      const assignments = await fetchPatientProviderAssignments(selectedPatient.id.toString());

      const providers: Provider[] = assignments.map(assignment => {
        const providerDetails = assignment.provider || {
          firstName: 'Unknown',
          lastName: 'Provider',
          discipline: assignment.assignmentType || 'Unknown'
        };

        const firstName = providerDetails?.firstName || 'Unknown';
        const lastName = providerDetails?.lastName || 'Provider';
        const discipline = normalizeDiscipline(providerDetails?.discipline || assignment.assignmentType);

        let middleName: string | undefined = undefined;
        if (assignment.provider && 'middleName' in assignment.provider) {
          middleName = assignment.provider.middleName;
        }

        return {
          id: assignment.assignmentId,
          userId: assignment.userId,
          assignmentId: assignment.assignmentId,
          name: `${firstName} ${lastName}`,
          discipline: discipline,
          firstName,
          lastName,
          middleName,
          startDate: assignment.startDate,
          endDate: assignment.endDate || '',
          isAssigned: true,
          assignmentType: assignment.assignmentType,
          expirationAlertLevel: assignment.expirationAlertLevel,
          expirationDaysRemaining: assignment.expirationDaysRemaining,
          expirationIsExpired: assignment.expirationIsExpired
        };
      });

      setAssignedProviders(providers);
    } catch (err) {
      console.error('Error refreshing provider assignments:', err);
      setError('Failed to refresh providers. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // State for date handling
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]); // Default to today
  const [endDate, setEndDate] = useState<string>(""); // Empty string means no expiration
  const [noExpiration, setNoExpiration] = useState<boolean>(true); // Default to no expiration

  // Function to add the selected provider
  const addProvider = async (): Promise<void> => {
    if (!selectedPatient?.id || !selectedProvider?.userId) {
      console.error('Cannot add provider: Missing patient ID or provider user ID');
      setError("Cannot add provider: Missing patient ID or provider ID");
      return;
    }

    // Validate start date
    if (!startDate) {
      setError("Start date is required");
      return;
    }

    // Validate end date if no expiration is unchecked
    if (!noExpiration && !endDate) {
      setError("Please select an end date or check 'No Expiration'");
      return;
    }

    setIsLoading(true);
    try {
      const assignmentType = selectedProvider.discipline || 'Unknown';
      // Use selected end date, or null if "no expiration" is checked
      const finalEndDate = noExpiration ? null : endDate || null;

      // Try to call the API first
      try {
        await assignProviderToPatient(
          selectedPatient.id.toString(),
          selectedProvider.userId,
          assignmentType,
          startDate,
          finalEndDate
        );

        // Clear any existing errors
        setError(null);

        // Refresh the list of assigned providers
        await fetchAssignedProviders(selectedPatient.id.toString());

        // Reset UI state
        setShowAddModal(false);
        setSelectedProvider(null);
      } catch (apiError: unknown) {
        // EX-08: the provider's type doesn't match the assignment's discipline (LVN is accepted for RN)
        if (isApiError(apiError) && (apiError.domainCode === 'PROVIDER_TYPE_MISMATCH' || apiError.statusCode === 422)) {
          setError(apiError.domainCode === 'PROVIDER_TYPE_MISMATCH'
            ? "This provider's type doesn't match the assignment's discipline, or they have no provider type. Set the provider type in User Management, or choose another provider."
            : `Failed to assign provider: ${apiError.message}`);
          return;
        }
        console.error('API Error assigning provider:', apiError instanceof Error ? apiError.message : 'unknown error');

        // Set specific error message if available
        if (apiError instanceof Error && apiError.message) {
          setError(`Failed to assign provider: ${apiError.message}`);
        } else {
          setError('Failed to assign provider. Please try again.');
        }

        // Fallback to local state for demo purposes
        if (selectedProvider) {
          // Create a new provider object with assignment info
          const newAssignedProvider = {
            ...selectedProvider,
            isAssigned: true,
            startDate: startDate,
            endDate: finalEndDate || '',
            assignmentId: `temp-${Date.now()}`,
            id: `temp-${Date.now()}`
          };

          setAssignedProviders([...assignedProviders, newAssignedProvider]);

          // Still close the modal in demo mode
          setShowAddModal(false);
          setSelectedProvider(null);
        }
      }
    } catch (err: any) {
      console.error('Error in addProvider function:', err);
      setError(`Failed to add provider: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to remove a provider
  const removeProvider = async (id: number | string): Promise<void> => {
    // Find the provider to be removed
    const providerToRemove = assignedProviders.find(provider => provider.id === id);

    if (!providerToRemove || !selectedPatient?.id) {
      console.error('Cannot remove provider: Provider not found or patient ID missing');
      setError('Cannot remove provider: Provider not found or patient ID missing');
      return;
    }

    if (!providerToRemove.userId) {
      console.error('Cannot remove provider: Missing provider user ID');
      setError('Cannot remove provider: Missing provider user ID');
      return;
    }

    setIsLoading(true);
    try {
      // Call API to unassign the provider using userId
      await unassignProviderFromPatient(
        selectedPatient.id.toString(),
        providerToRemove.userId.toString()
      );

      // Update the local state by filtering out the removed provider
      setAssignedProviders(assignedProviders.filter(provider => provider.id !== id));

      // Clear any existing errors
      setError(null);
    } catch (err: any) {
      console.error('Error unassigning provider:', err);
      // Show the specific error message if available
      setError(`Failed to remove provider: ${err.message || 'Please try again.'}`);

      // For temporary demo mode, still remove the provider from UI
      if (process.env.REACT_APP_DEMO_MODE === 'true') {
        setAssignedProviders(assignedProviders.filter(provider => provider.id !== id));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get provider initials for avatar
  const getProviderInitials = (provider: Provider): string => {
    // Get first initial of first name
    const firstInitial = provider.firstName ? provider.firstName.charAt(0) : '';
    // Get first initial of last name
    const lastInitial = provider.lastName ? provider.lastName.charAt(0) : '';

    return `${firstInitial}${lastInitial}`;
  };

  // Function to fetch providers from API
  const loadAvailableProviders = async (): Promise<void> => {
    setIsLoadingProviders(true);
    setError(null);
    try {
      const providers = await fetchAllProviders();

      // Map API user data to our Provider format
      // Normalize providerType to canonical discipline code (e.g. RN/LVN → RN)
      const mappedProviders: Provider[] = providers.map(user => ({
        id: user.id,
        userId: user.id,
        name: `${user.firstName} ${user.lastName}`,
        firstName: user.firstName,
        lastName: user.lastName,
        discipline: normalizeDiscipline(user.providerType),
        isAssigned: false
      }));

      setAvailableProviders(mappedProviders);
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load available providers');
    } finally {
      setIsLoadingProviders(false);
    }
  };

  // Function to get available providers (not already assigned to patient)
  const getAvailableProviders = (): Provider[] => {
    // Create a set of assigned provider userIds for quick lookup
    const assignedProviderIds = new Set(
      assignedProviders.map(provider => provider.userId)
    );

    // Filter providers that aren't already assigned to this patient
    let filteredProviders = availableProviders.filter(provider => !assignedProviderIds.has(provider.userId));

    // Apply discipline filter if not set to "All"
    // CASE_MGR matches CASE_MGR and CASE_MANAGER
    if (selectedDisciplineFilter !== "All") {
      filteredProviders = filteredProviders.filter(provider => {
        const d = (provider.discipline || '').toUpperCase();
        const f = selectedDisciplineFilter.toUpperCase();
        if (f === 'CASE_MGR') return d === 'CASE_MGR' || d === 'CASE_MANAGER';
        return d === f;
      });
    }

    // Apply search term filter if there's a search term
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase().trim();
      filteredProviders = filteredProviders.filter(provider =>
        provider.name.toLowerCase().includes(searchTermLower) ||
        provider.discipline.toLowerCase().includes(searchTermLower)
      );
    }

    return filteredProviders;
  };

  // Function to get available providers with pagination
  const getPaginatedAvailableProviders = (): Provider[] => {
    const filteredProviders = getAvailableProviders();
    const startIndex = (providerDialogPage - 1) * providersPerPage;
    return filteredProviders.slice(startIndex, startIndex + providersPerPage);
  };

  const filteredAssignedProviders = useMemo(() => {
    const query = assignedSearchTerm.toLowerCase().trim();
    return assignedProviders.filter(provider => {
      const providerName = `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.toLowerCase();
      const matchesSearch = !query || providerName.includes(query) || provider.discipline.toLowerCase().includes(query);
      const matchesDiscipline = assignedDisciplineFilter === 'All' || provider.discipline.toUpperCase() === assignedDisciplineFilter.toUpperCase();
      return matchesSearch && matchesDiscipline;
    });
  }, [assignedProviders, assignedSearchTerm, assignedDisciplineFilter]);

  const assignedProviderFilters: FilterConfig[] = useMemo(() => [
    {
      key: 'discipline',
      label: 'Discipline',
      value: assignedDisciplineFilter,
      options: [
        { value: 'All', label: 'All Disciplines' },
        ...providerDisciplines.map(discipline => ({
          value: discipline,
          label: getDisciplineConfig(discipline).label || discipline,
        })),
      ],
      onChange: setAssignedDisciplineFilter,
    },
  ], [assignedDisciplineFilter, providerDisciplines]);

  // Pagination controls component
  const PaginationControls = ({
    currentPage,
    setPage,
    totalItems,
    itemsPerPage
  }: {
    currentPage: number;
    setPage: (page: number) => void;
    totalItems: number;
    itemsPerPage: number;
  }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-center space-x-2 mt-2">
        <button
          onClick={() => setPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={getPaginationButtonClasses(isMobile, `px-2 py-1 text-xs rounded ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`)}
        >
          Previous
        </button>
        <span className="text-xs text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={getPaginationButtonClasses(isMobile, `px-2 py-1 text-xs rounded ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`)}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <>
      <GenericListView
        // ListView props
        title="Care Team"
        icon={<Users size={20} />}
        expanded={expanded}
        onToggleExpand={toggleExpanded}
        // Data props
        items={filteredAssignedProviders}
        keyExtractor={(provider) => provider.id.toString()}
        columns={[
          {
            key: 'provider',
            label: 'Provider Name',
            width: 'w-56',
            render: (provider: Provider) => (
              <div className="flex items-center">
                <div className="flex-shrink-0 h-8 w-8 mr-3">
                  <ProviderAvatar
                    userId={provider.userId || String(provider.id)}
                    initials={getProviderInitials(provider)}
                    discipline={provider.discipline}
                    size="sm"
                    displayMode="initials"
                    name={`${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim()}
                    photoUrl={provider.photoUrl}
                  />
                </div>
                <div className="text-sm font-medium text-slate-800">
                  {provider.firstName} {provider.middleName && `${provider.middleName} `}{provider.lastName}
                </div>
              </div>
            ),
          },
          {
            key: 'discipline',
            label: 'Discipline',
            width: 'w-32',
            align: 'center' as const,
            render: (provider: Provider) => (
              <DisciplinePill discipline={provider.discipline} />
            ),
          },
          {
            key: 'startDate',
            label: 'Start Date',
            width: 'w-32',
            align: 'center' as const,
            render: (provider: Provider) => (
              <div className="text-sm text-slate-600">
                {formatDate(provider.startDate || '')}
              </div>
            ),
          },
          {
            key: 'endDate',
            label: 'End Date',
            width: 'w-32',
            align: 'center' as const,
            render: (provider: Provider) => {
              const level = provider.expirationAlertLevel;
              const days = provider.expirationDaysRemaining;
              if (!provider.endDate || level === 'none') {
                return <div className="text-sm text-slate-400 italic">No Expiration</div>;
              }
              if (provider.expirationIsExpired || level === 'expired') {
                return <ExpirationPill status="expired" text="Expired" />;
              }
              if (level === 'red') {
                return <ExpirationPill status="red" text={`Expires in ${days}d`} />;
              }
              if (level === 'orange') {
                return <ExpirationPill status="orange" text={`Expires in ${days}d`} />;
              }
              if (level === 'yellow') {
                return <ExpirationPill status="yellow" text={`Expires in ${days}d`} />;
              }
              if (level === 'green') {
                return <ExpirationPill status="green" text={formatDate(provider.endDate)} />;
              }
              return <div className="text-sm text-slate-600">{formatDate(provider.endDate)}</div>;
            },
          },
        ]}
        actions={[
          {
            icon: <UserMinus size={14} />,
            label: 'Remove provider',
            onClick: (provider: Provider) => removeProvider(provider.id),
            className: `p-1.5 rounded transition-colors ${
              canUpdateProviders
                ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                : 'text-gray-400 cursor-not-allowed opacity-50'
            }`,
            disabled: !canUpdateProviders,
          },
        ]}
        // Mobile card config
        mobileCard={{
          avatar: (provider: Provider) => (
            <ProviderAvatar
              userId={provider.userId || String(provider.id)}
              initials={getProviderInitials(provider)}
              discipline={provider.discipline}
              size="lg"
              displayMode="initials"
              name={`${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim()}
              photoUrl={provider.photoUrl}
            />
          ),
          primaryText: (provider: Provider) => `${provider.firstName} ${provider.middleName ? provider.middleName + ' ' : ''}${provider.lastName}`,
          secondaryText: (provider: Provider) => `${formatDate(provider.startDate || '')} - ${formatDate(provider.endDate || '', true)}`,
          badges: (provider: Provider) => [
            {
              label: provider.discipline,
              className: getDisciplinePillClassName(provider.discipline),
            },
          ],
          actions: (provider: Provider) => canUpdateProviders ? [
            {
              icon: <UserMinus size={18} />,
              label: 'Remove',
              onClick: () => removeProvider(provider.id),
            },
          ] : [],
        }}
        // Action bar props
        onRefresh={handleRefresh}
        refreshing={refreshing}
        loading={isLoading}
        newButtonLabel="Add Provider"
        onNewClick={() => {
          setError(null); // Clear any errors
          setSelectedProvider(null); // Reset selected provider
          setSelectedDisciplineFilter("All"); // Reset discipline filter
          setSearchTerm(""); // Reset search term
          setProviderDialogPage(1); // Reset provider dialog pagination
          setStartDate(new Date().toISOString().split('T')[0]); // Reset to today
          setEndDate(""); // Clear end date
          setNoExpiration(true); // Default to no expiration
          setShowAddModal(true); // Show the modal
          loadAvailableProviders(); // Load providers from API when modal is opened
        }}
        showNew={!!selectedPatient && canUpdateProviders}
        searchValue={assignedSearchTerm}
        onSearchChange={setAssignedSearchTerm}
        searchPlaceholder="Search providers by name or discipline..."
        filters={assignedProviderFilters}
        resultCount={filteredAssignedProviders.length}
        filterTotalCount={assignedProviders.length}
        filterStorageKey="careTeam"
        desktopInlineFilters
        desktopFiltersCollapsedByDefault
        hideDesktopFilterResultCount
        // State props
        error={error}
        onDismissError={() => setError(null)}
        hasLoadedOnce={hasLoadedOnce}
        // Empty state props
        emptyStateIcon={<Users size={48} className="text-gray-300" />}
        emptyStateTitle="No providers assigned"
        emptyStateMessage="Start by assigning a provider using the button above."
        showList={!showAddModal}
      />

      {/* Add Provider Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            {isReadOnlyView && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-700">
                  You do not have permission to edit Care Team. Viewing in read-only mode.
                </p>
              </div>
            )}

            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h3 className="text-lg font-medium flex items-center text-left">
                <UserPlus size={18} className="mr-2 text-gray-700" />
                Add Provider
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProvider(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            {/* Search bar */}
            <div className="mb-4">
              <label htmlFor="providerSearch" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Search Providers:
              </label>
              <div className="relative">
                <input
                  id="providerSearch"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={isReadOnlyView}
                  placeholder="Search by name or discipline..."
                  className={`w-full py-2 px-3 pl-9 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-black focus:border-black text-base ${
                    isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                {searchTerm && (
                  <button
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                    onClick={() => setSearchTerm("")}
                  >
                    <X size={16} className="text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Discipline filter dropdown */}
            <div className="mb-4">
              <label htmlFor="disciplineFilter" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Filter by Discipline:
              </label>
              <div className="relative">
                <select
                  id="disciplineFilter"
                  className={`w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-black focus:border-black text-base ${
                    isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''
                  }`}
                  value={selectedDisciplineFilter}
                  onChange={(e) => setSelectedDisciplineFilter(e.target.value)}
                  disabled={isReadOnlyView}
                >
                  <option value="All">All Disciplines</option>
                  {providerDisciplines.map((discipline) => (
                    <option key={discipline} value={discipline}>{getDisciplineConfig(discipline).label || discipline}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <Filter size={16} />
                </div>
              </div>
            </div>

            {/* Provider List - showing only available providers */}
            <div className="max-h-48 overflow-y-auto mb-4 border rounded">
              {isLoadingProviders ? (
                <LoadingSpinner className="p-3" />
              ) : getAvailableProviders().length > 0 ? (
                getPaginatedAvailableProviders().map(provider => (
                  <div
                    key={provider.id}
                    className={`p-2 hover:bg-gray-100 cursor-pointer ${
                      selectedProvider?.id === provider.id ? 'bg-gray-50 border-l-4 border-black' : ''
                    }`}
                    onClick={() => setSelectedProvider(provider)}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 mr-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white font-medium ${getDisciplineConfig(provider.discipline).bar}`}>
                          {getProviderInitials(provider)}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="font-medium">{provider.name}</div>
                        <div className="text-sm">
                          <DisciplinePill discipline={provider.discipline} />
                        </div>
                      </div>
                      {selectedProvider?.id === provider.id &&
                        <CheckCircle size={16} className="text-green-600" />
                      }
                    </div>
                  </div>
                ))
              ) : error ? (
                <div className="p-3 text-center text-red-500">
                  <p>{error}</p>
                  <button
                    onClick={loadAvailableProviders}
                    className="mt-2 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="p-3 text-center text-gray-500">
                  <p>All available providers have already been assigned to this patient</p>
                </div>
              )}
            </div>

            {/* Pagination Controls for Provider List */}
            <PaginationControls
              currentPage={providerDialogPage}
              setPage={setProviderDialogPage}
              totalItems={getAvailableProviders().length}
              itemsPerPage={providersPerPage}
            />

            {/* Date Selection */}
            {selectedProvider && (
              <div className="mt-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                <h4 className="font-medium text-sm mb-2 text-gray-700 text-left">Assignment Dates:</h4>

                {/* Start Date */}
                <div className="mb-3">
                  <label htmlFor="startDate" className="block text-xs font-medium text-gray-600 mb-1 text-left">
                    Start Date:
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm text-base ${
                      isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''
                    }`}
                  />
                </div>

                {/* End Date with No Expiration checkbox */}
                <div>
                  <div className="flex items-center mb-1">
                    <input
                      type="checkbox"
                      id="noExpiration"
                      checked={noExpiration}
                      onChange={(e) => {
                        setNoExpiration(e.target.checked);
                        // Clear end date when switching to "No Expiration"
                        if (e.target.checked) {
                          setEndDate("");
                        }
                      }}
                      disabled={isReadOnlyView}
                      className={`mr-2 h-4 w-4 ${
                        isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    />
                    <label htmlFor="noExpiration" className="text-xs font-medium text-gray-600 text-left">
                      No Expiration
                    </label>
                  </div>

                  {!noExpiration && (
                    <div>
                      <label htmlFor="endDate" className="block text-xs font-medium text-gray-600 mb-1 text-left">
                        End Date:
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate} // Prevent selecting a date before start date
                        disabled={isReadOnlyView}
                        className={`w-full py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm text-base ${
                          isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''
                        }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProvider(null);
                }}
                className={getButtonClasses(isMobile, "flex text-sm items-center bg-gray-200 px-4 py-2 rounded text-gray-700 hover:bg-gray-300")}
              >
                <X size={16} className="mr-1" />
                Cancel
              </button>
              {canUpdateProviders && (
                <button
                  onClick={addProvider}
                  disabled={!selectedProvider || getAvailableProviders().length === 0 || isLoadingProviders || isReadOnlyView}
                  className={getButtonClasses(isMobile, `h-full px-3 sm:px-6 flex py-2 text-sm rounded-md transition-colors bg-slate-800 hover:bg-slate-700 text-white ${
                    selectedProvider && getAvailableProviders().length > 0 && !isLoadingProviders && !isReadOnlyView ? 'bg-black hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'
                  }`)}
                >
                  <CheckCircle size={16} className="mr-1" />
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CareTeam;
